import { useEffect, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FeatureFlag, type OrderDetailBlGroup } from '@metycle/shared';
import { useFeatureFlag } from '@/lib/feature-flags';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { InvoiceRow } from '../types';
import {
  InvoiceDialogContent,
  InvoiceDialogFormShell,
  InvoiceDialogRightPane,
} from './invoice-dialog-shell';
import { InvoiceCreatePdfProvider, useCreatePdfFileState } from './invoice-create-pdf-context';
import { ADVANCE_SUBMIT_FIELDS, type AdvanceSubmitValues } from './downpayment-invoice-schema';
import {
  applyPrefill,
  buildDefaults,
  type AdvancePrefill,
  type TicketContextForDownpayment,
} from './downpayment-invoice-payload';
import { ModelFormFields } from './model-form-fields';
import { ResolvedDocumentSection } from './resolved-document-section';
import { useSaveAdvanceMutation } from './use-save-advance-mutation';
import { useSuggestedNumberPrefill } from './use-suggested-number-prefill';
import {
  CreateCoverageSection,
  useCreateCoverage,
  type CreateCoverage,
} from './create-coverage-section';

export type { TicketContextForDownpayment } from './downpayment-invoice-payload';

export interface DownpaymentInvoiceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly existingInvoice: InvoiceRow | null;
  readonly ticket: TicketContextForDownpayment;
  readonly orderBls: readonly OrderDetailBlGroup[];
  readonly prefill?: AdvancePrefill;
  readonly onSaved: () => void;
}

function buildTitle(side: 'buy' | 'sell', existing: InvoiceRow | null): string {
  const verb = existing !== null ? 'Edit' : 'Create';
  const suffix = side === 'buy' ? 'Purchase Side' : 'Sales Side';
  return `${verb} Downpayment Invoice — ${suffix}`;
}

function buildDescription(existing: InvoiceRow | null): string {
  return existing !== null
    ? 'Edit any field while the invoice is in Draft. Issuing locks the invoice.'
    : 'Fill in the downpayment invoice details. Saving creates a Draft on this order.';
}

interface DialogFormBodyProps {
  readonly form: ReturnType<typeof useForm<AdvanceSubmitValues>>;
  readonly isPending: boolean;
  readonly side: 'buy' | 'sell';
  readonly existingInvoice: InvoiceRow | null;
  readonly coverage: CreateCoverage;
}

function DialogFormBody({ form, isPending, side, existingInvoice, coverage }: DialogFormBodyProps) {
  return (
    <>
      <ModelFormFields
        kind="Advance"
        side={side}
        register={form.register}
        control={form.control}
        errors={form.formState.errors}
        disabled={isPending}
      />
      <ResolvedDocumentSection invoice={existingInvoice} kind="Advance" />
      {coverage.active && <CreateCoverageSection coverage={coverage} />}
    </>
  );
}

type AdvanceForm = ReturnType<typeof useForm<AdvanceSubmitValues>>;

// The create picker's coverage + the save mutation, wired together so the
// submit carries the latest coverage and the footer can gate on its validity.
function useAdvanceSubmit(props: DownpaymentInvoiceDialogProps, form: AdvanceForm) {
  const { side, orderId, existingInvoice, orderBls, prefill, onSaved, onOpenChange } = props;
  const trackingEnabled = useFeatureFlag(FeatureFlag.INVOICING_ADVANCE_TRACKING_READ);
  const coverage = useCreateCoverage({
    orderBls,
    form,
    enabled: trackingEnabled && existingInvoice === null,
  });
  const mutation = useSaveAdvanceMutation({
    side,
    orderId,
    existingInvoice,
    ...(prefill !== undefined ? { aiExtractionId: prefill.aiExtractionId } : {}),
    getCoverage: () => (coverage.active ? coverage.state.coverage : undefined),
    onSaved,
    onOpenChange,
  });
  return {
    coverage,
    isPending: mutation.isPending,
    onSubmit: form.handleSubmit((values) => {
      mutation.mutate(values);
    }),
  };
}

function DialogForm({
  props,
  rightPane,
}: {
  readonly props: DownpaymentInvoiceDialogProps;
  readonly rightPane: ReactNode;
}) {
  const { open, side, orderId, existingInvoice, ticket, prefill } = props;
  const form = useForm<AdvanceSubmitValues>({
    resolver: zodResolver(ADVANCE_SUBMIT_FIELDS),
    defaultValues: applyPrefill(buildDefaults(side, existingInvoice, ticket), prefill),
  });

  useEffect(() => {
    if (open) form.reset(applyPrefill(buildDefaults(side, existingInvoice, ticket), prefill));
  }, [open, side, existingInvoice, ticket, prefill, form]);

  useSuggestedNumberPrefill(form, 'invoice_number', {
    invoiceType: 'Advance',
    direction: side === 'buy' ? 'Purchase' : 'Sale',
    orderId,
    enabled: open && existingInvoice === null,
  });

  const { coverage, isPending, onSubmit } = useAdvanceSubmit(props, form);

  return (
    <AdvanceFormLayout
      props={props}
      form={form}
      isPending={isPending}
      rightPane={rightPane}
      coverage={coverage}
      onSubmit={onSubmit}
    />
  );
}

function AdvanceFormLayout({
  props,
  form,
  isPending,
  rightPane,
  coverage,
  onSubmit,
}: {
  readonly props: DownpaymentInvoiceDialogProps;
  readonly form: ReturnType<typeof useForm<AdvanceSubmitValues>>;
  readonly isPending: boolean;
  readonly rightPane: ReactNode;
  readonly coverage: CreateCoverage;
  readonly onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}) {
  return (
    <form
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      noValidate
      className="flex min-h-0 flex-1 flex-col"
    >
      <InvoiceDialogFormShell
        rightPane={rightPane}
        footer={
          <AdvanceDialogFooter
            isPending={isPending}
            disabled={coverage.active && !coverage.state.isValid}
            onCancel={() => {
              props.onOpenChange(false);
            }}
          />
        }
      >
        <DialogHeader>
          <DialogTitle>{buildTitle(props.side, props.existingInvoice)}</DialogTitle>
          <DialogDescription>{buildDescription(props.existingInvoice)}</DialogDescription>
        </DialogHeader>
        <DialogFormBody
          form={form}
          isPending={isPending}
          side={props.side}
          existingInvoice={props.existingInvoice}
          coverage={coverage}
        />
      </InvoiceDialogFormShell>
    </form>
  );
}

function AdvanceDialogFooter({
  isPending,
  disabled,
  onCancel,
}: Readonly<{ isPending: boolean; disabled: boolean; onCancel: () => void }>) {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
        Cancel
      </Button>
      <Button type="submit" disabled={isPending || disabled}>
        {isPending ? 'Saving…' : 'Save'}
      </Button>
    </DialogFooter>
  );
}

// The advance editor shares the two-pane document surface with the other
// kinds: attach the supplier PDF and enter the fields in one place.
export function DownpaymentInvoiceDialog(props: DownpaymentInvoiceDialogProps) {
  const [pdfFile, setPdfFile] = useCreatePdfFileState(props.open, props.prefill?.file ?? null);
  const rightPane = (
    <InvoiceDialogRightPane
      existingInvoice={props.existingInvoice}
      file={pdfFile}
      onFileChange={setPdfFile}
      side={props.side}
    />
  );
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <InvoiceDialogContent>
        <InvoiceCreatePdfProvider value={{ file: pdfFile }}>
          {props.open && <DialogForm props={props} rightPane={rightPane} />}
        </InvoiceCreatePdfProvider>
      </InvoiceDialogContent>
    </Dialog>
  );
}
